// ─────────────────────────────────────────────
//  testMode — "which facility, which day, as whom" for a testing session.
//
//  Pure. No React, no Firestore, no theme. Reads and writes ONE localStorage
//  key and validates what comes back, because everything downstream — the db
//  swap, the pinned day, the previewed identity — trusts this shape.
//
//  ⚠️ BOOT-TIME, NEVER RUNTIME. sandbox/mode.js states the rule this obeys:
//  "`db` is referenced 90 times inside firebase/firestore.js and rebinding it
//  after listeners have attached leaves stale onSnapshot subscriptions that
//  fail SILENTLY." So entering and leaving testing mode are page reloads, and
//  this module only ever records the intent.
//
//  ⚠️ THIS IS NOT THE SANDBOX. The sandbox boots a SYNTHETIC facility
//  (`sbx` / "My Test Facility", config/roles.js:246). Testing mode boots a
//  SNAPSHOT OF A REAL FACILITY ON A REAL DATE. They share the local data plane
//  and nothing else, and they must never both be on — a boot that believed
//  both would seed demo data over a real snapshot.
//
//  ⚠️ EVERY READ IS GUARDED. localStorage throws in Safari private mode, and
//  config.js imports this at MODULE SCOPE, where a throw takes the whole module
//  graph down before React mounts. That failure has shipped in this app before.
// ─────────────────────────────────────────────

export const TEST_MODE_KEY = "atc.testMode";

// The local store the snapshot lives in. Distinct from the sandbox's
// "atc-sandbox-v1" so a testing snapshot can never be served to a demo boot,
// or vice versa.
export const TEST_MODE_PERSIST_KEY = "atc-testmode-v1";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A testing-mode config is only usable when it names all three things the
 * owner asked to choose up front: which facility, which day, and as whom.
 *
 * Fails closed. A half-written config (an interrupted entry, a hand-edited
 * key, an older shape from a previous release) is NOT a testing session — it
 * is a normal boot. Returning something partial here would pin the app to a
 * facility with no date, which renders as "today at the wrong hospital".
 */
export function isValidTestMode(cfg) {
  if (!cfg || typeof cfg !== "object") return false;
  if (typeof cfg.facilityId !== "string" || !cfg.facilityId.trim()) return false;
  // The date is the FACILITY's day key (dayKey + facilityTz), never
  // rosterDateKey. Shape-check it here; the caller owns which clock made it.
  if (typeof cfg.date !== "string" || !DATE_RE.test(cfg.date)) return false;
  if (typeof cfg.viewAsUid !== "string" || !cfg.viewAsUid.trim()) return false;
  return true;
}

export function readTestMode() {
  try {
    const raw = localStorage.getItem(TEST_MODE_KEY);
    if (!raw) return null;
    const cfg = JSON.parse(raw);
    return isValidTestMode(cfg) ? cfg : null;
  } catch {
    // Unparseable, unreadable, or a hostile storage. Not a testing session.
    return null;
  }
}

// True when this boot should serve the local plane instead of live Firestore.
// config.js calls this before Firestore exists.
export function isTestModeBoot() {
  return readTestMode() !== null;
}

/**
 * Record the intent. The CALLER reloads — this never navigates, so a caller
 * can write the snapshot first and reload once.
 *
 * Returns the stored config, or null when the input was not usable. A silent
 * false-success here would reload into a normal boot and look like the feature
 * simply did nothing.
 */
export function writeTestMode({
  facilityId, date, viewAsUid, viewAsName = "", facilityName = "", missing = [],
} = {}) {
  const cfg = {
    facilityId, date, viewAsUid,
    viewAsName: viewAsName || "",
    facilityName: facilityName || "",
    // ⚠️ CARRIED ACROSS THE RELOAD ON PURPOSE. The manifest is computed before
    // entry and would otherwise be lost the moment the page reloads — and then
    // nothing could tell a tester that a collection is missing rather than
    // empty, which is the whole reason the manifest exists. Only the names are
    // kept; the counts are not needed after entry.
    missing: Array.isArray(missing) ? missing.filter((c) => typeof c === "string") : [],
    enteredAt: new Date().toISOString(),
  };
  if (!isValidTestMode(cfg)) return null;
  try {
    localStorage.setItem(TEST_MODE_KEY, JSON.stringify(cfg));
    return cfg;
  } catch {
    return null;
  }
}

// Clearing the FLAG is not leaving. The snapshot store must be destroyed too or
// the next entry silently re-serves a stale day — see the spec's exit section
// and the note that nothing currently calls the sandbox's own exit.
export function clearTestMode() {
  try { localStorage.removeItem(TEST_MODE_KEY); } catch { /* ignore */ }
}

/**
 * The banner sentence. One place, so the two surfaces that show it cannot word
 * it differently, and so it always names all THREE facts: a tester who has
 * forgotten which day they are standing in is the failure the whole screen
 * exists to prevent.
 */
export function testModeLabel(cfg) {
  if (!isValidTestMode(cfg)) return "";
  const who = cfg.viewAsName || cfg.viewAsUid;
  const where = cfg.facilityName || cfg.facilityId;
  return `TESTING — ${where} · ${cfg.date} · as ${who}`;
}
